import { clearNuxtData } from '#app';
import consola from 'consola';

/** Single in-flight refresh promise so concurrent 401s share one server refresh call. */
let inFlightRefreshPromise = null;

/**
 * Client-side Auth0 token refresh composable.
 * When API calls return 401, refresh via POST /api/account/auth/refresh (HttpOnly cookie)
 * and apply new access/ID tokens to the session store so the request can be retried
 * without reloading the page.
 */
export function useAuth0TokenRefresh() {
  const sessionStore = useSessionStore();
  const testingStore = useTestingStore();

  const isAuth0Enabled = testingStore.hasEnabledFeature('web_auth0InStaticweb');

  /**
   * Attempt to refresh Auth0 tokens via the server refresh endpoint.
   * Updates session store on success. Concurrent callers share one in-flight request.
   * @returns {Promise<{ accessToken: string, idToken: string, expiresIn: string } | null>}
   *   New tokens if refresh succeeded, null otherwise. Never includes the refresh token.
   */
  const tryRefreshTokens = async () => {
    if (false) {
      return null;
    }

    if (!isAuth0Enabled) {
      return null;
    }

    if (inFlightRefreshPromise) {
      return inFlightRefreshPromise;
    }

    const doRefresh = async () => {
      try {
        const newTokens = await $fetch('/api/account/auth/refresh', { method: 'POST' });

        if (!newTokens?.accessToken) {
          consola.error('### Auth0 token refresh failed or returned no access token');
          return null;
        }

        sessionStore.setIdToken(newTokens.idToken);
        sessionStore.setApiToken({
          accessToken: newTokens.accessToken,
          refreshToken: null,
          expiresIn: newTokens.expiresIn,
        });

        // Access/ID cookies are set by the server response; keep client cookies in sync if needed
        sessionStore.setSessionCookies({
          idToken: newTokens.idToken,
          apiToken: {
            accessToken: newTokens.accessToken,
            refreshToken: null,
            expiresIn: newTokens.expiresIn,
          },
        });

        clearNuxtData();

        return {
          accessToken: newTokens.accessToken,
          idToken: newTokens.idToken,
          expiresIn: newTokens.expiresIn,
        };
      } catch (error) {
        consola.error(`### Auth0 token refresh failed: ${error?.message || 'Unknown error'}`);
        return null;
      } finally {
        inFlightRefreshPromise = null;
      }
    };

    inFlightRefreshPromise = doRefresh();
    return inFlightRefreshPromise;
  };

  return {
    tryRefreshTokens,
    isAuth0Enabled,
  };
}
